import type React from "react";

import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMatch, useNavigate } from "react-router-dom";
import styled from "styled-components";

interface MenuItemProps {
  to: string;
  label: string;
  icon: IconDefinition;
  iconActive?: IconDefinition;
  notificationCount?: number;
  dataTestId?: string;
}

export const MenuItem: React.FC<MenuItemProps> = ({ to, label, icon, iconActive, notificationCount, dataTestId }) => {
  const navigate = useNavigate();
  const match = useMatch("*");
  const toCompare = to.split("/")[3];
  const original = match?.pathname.split("/")[3];
  const isActive = original === toCompare;

  const iconToShow = isActive && iconActive ? iconActive : icon;

  const getContent = () => (
    <>
      <FontAwesomeIcon icon={iconToShow} data-testid="item-icon" />
      <LabelWrapper>{label}</LabelWrapper>
      {!!notificationCount && (
        <NotificationBadge data-testid="unread-comments-badge">{notificationCount}</NotificationBadge>
      )}
    </>
  );

  return (
    <Item data-testid={dataTestId} onClick={() => navigate(to)} $active={isActive}>
      {getContent()}
    </Item>
  );
};

const Item = styled.div<{ $active: boolean }>`
  cursor: pointer;
  height: 46px;
  box-sizing: border-box;
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
  user-select: none;
  border: 1px solid ${props => props.theme.colors.redesign.db20};
  background: ${props => props.theme.colors.white};
  color: ${props => (props.$active ? props.theme.colors.redesign.b100 : props.theme.colors.redesign.db100)};
  transition: color 0.25s ease-in-out;
  ${props =>
    props?.$active &&
    `
    border: 1px solid ${props.theme.colors.redesign.b100};
    background: ${props.theme.colors.redesign.b10};
  `}
  &:hover {
    background: ${props => props.theme.colors.redesign.b10};
    color: ${props => props.theme.colors.redesign.b100};
  }
  &:first-child {
    border-top-left-radius: ${props => props.theme.borderRadius.basic};
    border-bottom-left-radius: ${props => props.theme.borderRadius.basic};
    margin-left: 0;
  }
  &:last-child {
    border-top-right-radius: ${props => props.theme.borderRadius.basic};
    border-bottom-right-radius: ${props => props.theme.borderRadius.basic};
  }

  ${props => props.theme.belowBreakpoint} {
    border-width: 1px 0 !important;
    border-radius: 0 !important;
  }
`;

const LabelWrapper = styled.span`
  color: inherit;
  font-size: 14px;
  font-weight: 400;

  ${props => props.theme.belowBreakpoint} {
    display: none;
  }
`;

const NotificationBadge = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  border-radius: 4px;
  padding: 2px 6px;
  background-color: ${props => props.theme.colors.redesign.r100};
  color: ${props => props.theme.colors.white};
`;
